import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { fetchItemChargeCandidates } from './detailApi';
import type { ItemChargeCandidate } from './detailTypes';
import './quotes.css';

// ─────────────────────────────────────────────────────────────────────────────
// The "add item charge" popup behind Item Charge ▸ Add.
// Port of legacy `ItemChargePricing_ListView_Quotes_Popup`: every live item
// charge, with the ones already on the quote pre-ticked (legacy :3080-3082).
// One row per item charge on a quote, so an already-ticked item adds nothing.
// ─────────────────────────────────────────────────────────────────────────────

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const COLUMNS: AppColumn<ItemChargeCandidate>[] = [
  { field: 'itemCode',    header: 'ItemCode',    width: 120 },
  { field: 'itemName',    header: 'ItemName',    width: 220 },
  { field: 'category',    header: 'Category',    width: 140 },
  { field: 'unit',        header: 'Unit',        width: 90 },
  {
    field: 'unitPrice', header: 'UnitPrice', width: 110, align: 'right', type: 'number',
    render: (r) => money.format(r.unitPrice),
  },
  { field: 'description', header: 'Description' },
];

interface Props {
  open: boolean;
  /** Item charges already on the quote (their ItemPrice ids). */
  existingIds: Set<string>;
  /** The newly ticked item charges only. */
  onAdd: (picked: ItemChargeCandidate[]) => void;
  onClose: () => void;
}

export function ItemChargePickerDialog({ open, existingIds, onAdd, onClose }: Props) {
  const notify = useNotify();
  const [candidates, setCandidates] = useState<ItemChargeCandidate[]>([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState<Set<string | number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSearch('');
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const rows = await fetchItemChargeCandidates();
        if (cancelled) return;
        setCandidates(rows);
        setSelected(new Set(rows.filter((r) => existingIds.has(r.itemPriceId.toLowerCase()))
                                .map((r) => r.itemPriceId)));
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the item charges.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      [c.itemCode, c.itemName, c.category, c.unit, c.description]
        .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [candidates, search]);

  function accept() {
    const ticked = candidates.filter((c) => selected.has(c.itemPriceId));
    if (ticked.length === 0) {
      // Same wording as the price-code popup (legacy :2536).
      notify.info('Select atleast one checkbox.');
      return;
    }
    onAdd(ticked.filter((c) => !existingIds.has(c.itemPriceId.toLowerCase())));
  }

  return (
    <AppDialog
      open={open}
      title="Item Charge"
      width={900}
      className="dlg-center qt-picker"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={accept} disabled={loading}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<ItemChargeCandidate>
        rows={rows}
        columns={COLUMNS}
        getRowId={(r) => r.itemPriceId}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        hideHeaderTools
        emptyText={search ? 'No results match your search.' : 'No item charges have been set up.'}
      />
    </AppDialog>
  );
}
